/**
 * BugSnag Integration doc: https://docs.google.com/document/d/1eLCpukDYq0tgqiOvBpYH-gK_ZeYsWD0YpiWA43VWa1w/edit?pli=1#heading=h.c0asm89s786e
 */
import BugsnagPerformance, { DefaultRoutingProvider } from '@bugsnag/browser-performance';
import Bugsnag, { Event } from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';

import {
  LEVER_BUGSNAG_IS_ENABLED,
  LEVER_BUGSNAG_PERFORMANCE_IS_ENABLED,
} from 'Lib/ConfigService/levers';
import { debugI13nBugsnag as debug } from 'Lib/debug';
import {
  AppealableBannedUserError,
  BannedUserError,
  BlacklistedUserError,
  FacebookAPIError,
  FacebookSDKUninitializedError,
  RateLimitedError,
  RequestTimeoutError,
  RSOBannedUserError,
  SelfieChallengeV2Error,
  ServerSideAPIError,
  UnauthorizedAPIError,
  UnderageUserError,
  UnderReviewUserError,
} from 'Lib/errors';
import { getLeverValue } from 'Lib/utils/getLeverValue';
import { stripID } from 'Lib/utils/pathUtils';

import BugsnagEventBuilder from './utils/BugsnagEventBuilder';

const shouldIgnore = (event: BugsnagEventBuilder) =>
  event.isNotInstanceOf([Error]) ||
  event.isInstanceOf([
    AppealableBannedUserError,
    BannedUserError,
    BlacklistedUserError,
    FacebookAPIError,
    FacebookSDKUninitializedError,
    RateLimitedError,
    RequestTimeoutError,
    RSOBannedUserError,
    // TODO: consider to report the API error since it's no longer being captured in the breadcrumb of the Saga errors
    ServerSideAPIError,
    SelfieChallengeV2Error,
    UnauthorizedAPIError,
    UnderageUserError,
    UnderReviewUserError,
  ]);

const shouldSample = (event: BugsnagEventBuilder) => Math.random() < event.getSampleRate();

/**
 * Get current app version
 *
 * @returns { String } version or undefined
 */
const getAppVersion = () => {
  const pattern = /^\d+\.\d+\.\d+$/;
  return process.env.VERSION.match(pattern)?.[0];
};

/**
 * Get current node environment
 * NOTE: use beta instead of staging to stay consistent across platforms
 *
 * @returns { String } beta (staging), production or undefined
 */
const getReleaseStage = () => {
  if (process.env.NODE_ENV === 'production') {
    // Additional justification to prevent staging from being logged as production env
    return __PRODTEST__ ? 'beta' : 'production';
  }
};

/**
 * Resolve the route from the URL
 * TODO: A better route matched mechanism is required once we decide to use Bugsnag performance monitoring
 * @param url the current URL
 * @returns { String } route
 */
const resolveRoute = (url: URL): string => {
  const { pathname } = url;
  // Deal with the promo code param
  if (pathname.startsWith('/vip/')) {
    return '/vip/:promoCode';
  }
  // Deal with the otp param
  if (
    pathname.startsWith('/app/campaigns/') ||
    pathname.startsWith('/app/link/') ||
    pathname.startsWith('/deeplink/')
  ) {
    const segments = pathname.split('/');
    segments[segments.length - 1] = ':otp';
    return segments.join('/');
  }
  return stripID(url.pathname);
};

const init = ({ bugsnagID }: { bugsnagID: string }) => {
  if (!__TEST__ && (!__CLIENT__ || !bugsnagID)) {
    return;
  }

  debug('initialized bugsnag plugin');

  const isEnabled = getLeverValue(LEVER_BUGSNAG_IS_ENABLED);
  const isPerfEnabled = getLeverValue(LEVER_BUGSNAG_PERFORMANCE_IS_ENABLED);
  const appVersion = getAppVersion();
  const releaseStage = getReleaseStage();

  // Do not run Bugsnag in development env (localhost) to avoid unnecessary logging
  if (isEnabled) {
    Bugsnag.start({
      apiKey: bugsnagID,
      appVersion,
      autoDetectErrors: true,
      enabledBreadcrumbTypes: ['error', 'log', 'navigation', 'user'],
      collectUserIp: false,
      enabledErrorTypes: {
        unhandledExceptions: true,
        unhandledRejections: false,
      },
      // TODO: increase maxBreadcrumbs to 500 once Bugsnag supports it on web
      maxBreadcrumbs: 100,
      /**
       * Whether to report an event to Bugsnag
       * @param event an error event
       * @returns { boolean } result
       */
      onError: (event: Event) => {
        const decoratedEvent = new BugsnagEventBuilder(event);
        if (!shouldSample(decoratedEvent) || shouldIgnore(decoratedEvent)) {
          return false;
        }
        return true;
      },
      plugins: [new BugsnagPluginReact()],
      releaseStage,
    });
  }

  if (isPerfEnabled) {
    BugsnagPerformance.start({
      apiKey: bugsnagID,
      appVersion,
      releaseStage,
      routingProvider: new DefaultRoutingProvider(resolveRoute),
    });
  }
};

export default init;
